import { ForceUpdateStatus, SocketEvent } from "@common/SocketEvents";
import {
  Box,
  Button,
  ButtonBase,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { useAppContext } from "../contexts/App.context";
import { useSnackbar } from "../contexts/Snackbar.context";
import { useSocket } from "../contexts/Socket.context";

const REACTIONS = ["👏", "🎉", "❤️", "🔥", "😂", "😮", "👍", "🎤", "🎵", "🥳", "💯", "😍"];

// Desktop builds older than this ignore reactions and remote updates.
const MIN_DESKTOP_VERSION = "1.0.29";
// Text messages need this one.
const MIN_MESSAGE_VERSION = "1.0.32";
// Same limit as the server.
const MAX_MESSAGE_LENGTH = 80;

const isAtLeast = (version: string, min: string) => {
  const a = version.split(".").map(Number);
  const b = min.split(".").map(Number);
  for (let i = 0; i < Math.max(a.length, b.length); i++) {
    const diff = (a[i] || 0) - (b[i] || 0);
    if (diff !== 0) return diff > 0;
  }
  return true;
};

const UPDATE_MESSAGES: Record<ForceUpdateStatus["state"], (s: ForceUpdateStatus) => string> = {
  checking: () => "Desktop: checking for updates…",
  "up-to-date": (s) => `Desktop is up to date (v${s.currentVersion})`,
  downloading: (s) => `Desktop: downloading v${s.version}…`,
  installing: (s) => `Desktop: restarting into v${s.version}`,
  failed: (s) => `Desktop update failed${s.message ? `: ${s.message}` : ""}`,
  unsupported: (s) => `Desktop can't update: ${s.message ?? "not supported"}`,
};

// Admin-only controls for the desktop app's screen. Kept in their own panel,
// apart from the settings switches, so they aren't tapped by mistake.
const DesktopControls = () => {
  const { socket, sendRequest } = useSocket();
  const { data } = useAppContext();
  const addSnackbar = useSnackbar();
  // addSnackbar changes every render; read the latest from the socket listener.
  const addSnackbarRef = useRef(addSnackbar);
  addSnackbarRef.current = addSnackbar;
  const { isPlayerConnected, playlistData } = data;
  const desktopVersion = playlistData.playerVersion;

  // The emoji just sent, highlighted for a moment as feedback.
  const [sentEmoji, setSentEmoji] = useState<string | null>(null);
  const sentTimer = useRef<ReturnType<typeof setTimeout>>();
  const [message, setMessage] = useState("");
  const [sendingMessage, setSendingMessage] = useState(false);
  const [confirmUpdate, setConfirmUpdate] = useState(false);
  const [updating, setUpdating] = useState(false);

  // Older desktops don't report a version and ignore these commands.
  const supported =
    isPlayerConnected &&
    !!desktopVersion &&
    isAtLeast(desktopVersion, MIN_DESKTOP_VERSION);
  const unavailableReason = !isPlayerConnected
    ? "The desktop app is not connected."
    : !supported
      ? `The desktop app is ${desktopVersion ? `v${desktopVersion}` : "an older version"}. Update it once by hand to v${MIN_DESKTOP_VERSION} or later to use these.`
      : "";
  const messagesSupported =
    supported && isAtLeast(desktopVersion, MIN_MESSAGE_VERSION);

  useEffect(() => {
    const onStatus = (status: ForceUpdateStatus) => {
      const done = status.state !== "checking" && status.state !== "downloading";
      if (done) setUpdating(false);
      addSnackbarRef.current(
        UPDATE_MESSAGES[status.state](status),
        status.state === "failed" || status.state === "unsupported"
          ? "error"
          : "info"
      );
    };
    socket.on(SocketEvent.FORCE_UPDATE_STATUS, onStatus);
    return () => {
      socket.off(SocketEvent.FORCE_UPDATE_STATUS, onStatus);
    };
  }, [socket]);

  // Sent on tap, no confirm: tapping again sends another.
  const sendReaction = async (emoji: string) => {
    setSentEmoji(emoji);
    clearTimeout(sentTimer.current);
    sentTimer.current = setTimeout(() => setSentEmoji(null), 400);
    const result = await sendRequest(SocketEvent.SEND_REACTION, { emoji });
    if (result !== true && result?.error) addSnackbar(result.error, "warning");
  };

  const sendMessage = async () => {
    const text = message.trim();
    if (!text) return;
    setSendingMessage(true);
    const result = await sendRequest(SocketEvent.SEND_SCREEN_MESSAGE, { text });
    setSendingMessage(false);
    if (result !== true && result?.error) {
      addSnackbar(result.error, "warning");
      return;
    }
    setMessage("");
  };

  const startUpdate = async () => {
    setConfirmUpdate(false);
    setUpdating(true);
    const result = await sendRequest(SocketEvent.FORCE_UPDATE, null);
    if (result !== true && result?.error) {
      setUpdating(false);
      addSnackbar(result.error, "warning");
    }
  };

  return (
    <Paper
      variant="outlined"
      sx={{ p: 2, mb: 4, borderColor: "primary.main", borderWidth: 2 }}
    >
      <Typography variant="h5">Desktop screen</Typography>
      <Typography variant="caption" component="p" sx={{ mb: 2 }}>
        Admin only. These act on the desktop app right away.
        {desktopVersion && ` Connected: v${desktopVersion}.`}
      </Typography>
      {unavailableReason && (
        <Typography variant="body2" color="warning.main" sx={{ mb: 2 }}>
          {unavailableReason}
        </Typography>
      )}

      <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
        Send a reaction
      </Typography>
      <Typography variant="caption" component="p" sx={{ mb: 1 }}>
        Tap an emoji to show it on the screen.
      </Typography>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(52px, 1fr))",
          gap: 1,
          mb: 1.5,
        }}
      >
        {REACTIONS.map((item) => (
          <ButtonBase
            key={item}
            aria-label={`Send ${item}`}
            onClick={() => sendReaction(item)}
            disabled={!supported}
            sx={{
              height: 52,
              fontSize: 28,
              borderRadius: 2,
              border: 2,
              borderColor: item === sentEmoji ? "primary.main" : "transparent",
              background:
                item === sentEmoji
                  ? "rgba(255,255,255,.16)"
                  : "rgba(255,255,255,.04)",
              transform: item === sentEmoji ? "scale(1.12)" : "none",
              transition: "transform .15s, background .15s",
              opacity: supported ? 1 : 0.4,
            }}
          >
            {item}
          </ButtonBase>
        ))}
      </Box>

      <Typography variant="subtitle1" sx={{ fontWeight: 500, mt: 2.5 }}>
        Send a message
      </Typography>
      <Typography variant="caption" component="p" sx={{ mb: 1 }}>
        {messagesSupported || !supported
          ? "Shows on the screen for a few seconds."
          : `Needs desktop v${MIN_MESSAGE_VERSION} or later. It updates itself on its next start.`}
      </Typography>
      <Box
        component="form"
        onSubmit={(e) => {
          e.preventDefault();
          sendMessage();
        }}
        sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}
      >
        <TextField
          size="small"
          fullWidth
          autoComplete="off"
          placeholder="Happy birthday!"
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          disabled={!messagesSupported}
          inputProps={{
            maxLength: MAX_MESSAGE_LENGTH,
            "aria-label": "Message to show on the screen",
          }}
        />
        <Button
          type="submit"
          variant="contained"
          disabled={!messagesSupported || sendingMessage || !message.trim()}
        >
          Send
        </Button>
      </Box>

      <Divider sx={{ my: 2.5 }} />

      <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
        Update the desktop app
      </Typography>
      <Typography variant="caption" component="p" sx={{ mb: 1 }}>
        Installs the latest version right away if there is one. The desktop app
        restarts and playback stops for a moment.
      </Typography>
      <Button
        variant="outlined"
        color="warning"
        disabled={!supported || updating}
        onClick={() => setConfirmUpdate(true)}
        startIcon={updating && <CircularProgress color="inherit" size={16} />}
      >
        {updating ? "Updating…" : "Update now"}
      </Button>

      <Dialog open={confirmUpdate} onClose={() => setConfirmUpdate(false)}>
        <DialogTitle>Update the desktop app now?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            If a newer version is available, the desktop app downloads it and
            restarts immediately. The song playing now stops for a moment.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmUpdate(false)}>Cancel</Button>
          <Button color="warning" onClick={startUpdate}>
            Update now
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default DesktopControls;
